/**
 * Authentication Validation Utilities
 *
 * Provides domain validation and claim extraction helpers for MSAL authentication.
 * These utilities support the "Trilogy Gate" - strict domain enforcement for
 * @trilogynet.com email addresses.
 */

import type { AccountInfo } from '@azure/msal-browser'

// ============================================================================
// Constants
// ============================================================================

const TRILOGY_DOMAIN = '@trilogynet.com'

// ============================================================================
// Domain Validation
// ============================================================================

/**
 * Validate email domain against Trilogy domain
 *
 * Checks if email ends with @trilogynet.com. This is a UX-level check only.
 * Backend MUST re-validate tenant ID (tid) from the JWT for security.
 *
 * @param emailAddress - Email address to validate (e.g., "user@trilogynet.com")
 * @returns true if email is @trilogynet.com domain, false otherwise
 */
export function validateDomain(emailAddress: string): boolean {
  if (!emailAddress || typeof emailAddress !== 'string') {
    return false
  }
  const domain = emailAddress.toLowerCase().split('@').pop()
  return domain === 'trilogynet.com'
}

// ============================================================================
// Claim Extraction
// ============================================================================

/**
 * Extract email from MSAL account response
 *
 * Tries multiple sources for email:
 * 1. account.idTokenClaims.preferred_username (primary)
 * 2. account.idTokenClaims.email (fallback)
 * 3. account.username (fallback)
 *
 * @param account - MSAL account object
 * @returns Email address string, or empty string if not found
 */
export function extractEmailFromMsalResponse(account: AccountInfo | null): string {
  if (!account) return ''

  // Try idTokenClaims first (more reliable)
  const idTokenClaims = account.idTokenClaims as any
  if (idTokenClaims?.preferred_username) {
    return idTokenClaims.preferred_username as string
  }
  if (idTokenClaims?.email) {
    return idTokenClaims.email as string
  }

  // Fallback to account.username
  if (account.username) {
    return account.username
  }

  return ''
}

/**
 * Extract tenant ID from MSAL account response
 *
 * The tenant ID (tid) claim is the cryptographic proof of which Azure tenant
 * the user belongs to. Backend MUST validate this matches the expected
 * TRILOGY_TENANT_ID constant.
 *
 * @param account - MSAL account object
 * @returns Tenant ID string, or null if not found
 */
export function extractTenantIdFromMsalResponse(
  account: AccountInfo | null
): string | null {
  if (!account) return null

  const idTokenClaims = account.idTokenClaims as any
  if (idTokenClaims?.tid) {
    return idTokenClaims.tid as string
  }

  return null
}

